import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Button } from 'antd';
import styles from './Firewalls.module.less';
import AppTable from '../AppTable';
import AppConfirmDialog from '../AppConfirmDialog';
import CcxIconDeleteOutlined from '../ccx/icons/CcxIconDeleteOutlined';
import { FirewallTableInterface } from '../../types/FirewallTable';
import Can from '../../core/providers/Can';
import { CAN_DELETE_FIREWALL } from '../../core/PermissionRoles';

export const FIREWALLS_PAGE_SIZE = 10;

interface FirewallsTableProps {
    testId?: string;
    firewalls: FirewallTableInterface[];
    // Delete a single rule (the container wires the service call + notifications).
    onDeleteRule: (record: FirewallTableInterface) => Promise<void>;
    // Delete several rules at once (the container decides how to call the backend).
    onDeleteSelected: (records: FirewallTableInterface[]) => Promise<void>;
    expandedRowRender?: (record: FirewallTableInterface) => ReactNode;
    rowExpandable?: (record: FirewallTableInterface) => boolean;
}

function FirewallsTable({
    testId = 'FirewallsTable',
    firewalls,
    onDeleteRule,
    onDeleteSelected,
    expandedRowRender,
    rowExpandable,
}: FirewallsTableProps): ReactElement {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [deletingSelected, setDeletingSelected] = useState(false);

    // Keep the current page valid when the list shrinks (e.g. after a delete
    // removes the last row on the last page) so we never land on an empty page.
    const lastPage = Math.max(
        1,
        Math.ceil(firewalls.length / FIREWALLS_PAGE_SIZE)
    );
    useEffect(() => {
        if (currentPage > lastPage) {
            setCurrentPage(lastPage);
        }
    }, [lastPage, currentPage]);

    // Drop selections that point to rows that no longer exist.
    useEffect(() => {
        setSelectedRowKeys((prev) =>
            prev.filter((key) => firewalls.some((rule) => rule.source === key))
        );
    }, [firewalls]);

    const columns = [
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 85,
            render: (text: string, record: FirewallTableInterface) => (
                <Can permission={CAN_DELETE_FIREWALL} showTooltip={true}>
                    <div className={styles.FirewallsOptionsColumn}>
                        <AppConfirmDialog
                            critical={true}
                            onOk={() => onDeleteRule(record)}
                            content="The source will be removed."
                            actionIcon={<CcxIconDeleteOutlined />}
                        />
                    </div>
                </Can>
            ),
        },
    ];

    const onConfirmDeleteSelected = async () => {
        const records = firewalls.filter((rule) =>
            selectedRowKeys.includes(rule.source)
        );
        setDeletingSelected(true);
        try {
            await onDeleteSelected(records);
            setSelectedRowKeys([]);
        } finally {
            setDeletingSelected(false);
        }
    };

    return (
        <div data-testid={testId}>
            {selectedRowKeys.length > 0 && (
                <Can permission={CAN_DELETE_FIREWALL} showTooltip={false}>
                    <div
                        className={styles.FirewallsBulkActions}
                        data-testid={`${testId}BulkActions`}
                    >
                        <AppConfirmDialog
                            critical={true}
                            onOk={onConfirmDeleteSelected}
                            content={`${selectedRowKeys.length} source(s) will be removed.`}
                            label={
                                <Button
                                    danger={true}
                                    loading={deletingSelected}
                                >
                                    Delete selected ({selectedRowKeys.length})
                                </Button>
                            }
                        />
                    </div>
                </Can>
            )}

            <AppTable
                columns={columns}
                data={firewalls}
                rowKey="source"
                expandedRowRender={expandedRowRender}
                rowExpandable={rowExpandable}
                customIcon={true}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys: any) =>
                        setSelectedRowKeys(keys as string[]),
                }}
                pagination={{
                    // Math.min guards the single render between a shrinking
                    // list and the clamp effect, so current is never out of range.
                    current: Math.min(currentPage, lastPage),
                    pageSize: FIREWALLS_PAGE_SIZE,
                    onChange: (page: number) => setCurrentPage(page),
                    hideOnSinglePage: true,
                }}
            />
        </div>
    );
}

export default FirewallsTable;
